import { DOCUMENT } from '@angular/common';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject, input, output, signal } from '@angular/core';
import { effectiveScheduleRule, ScheduleChange, ScheduleExceptions } from '@jms/model/scheduling';
import { ScheduleRule } from '@jms/model/scheduling/schedule.rule';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { ButtonComponent, ButtonDisplayStyle, ButtonSize } from '@workbuddy/ui/v2/components/button';
import { CalendarDate } from '@workbuddy/ui/v2/components/calendar';
import { ExceptionPopoverMode, ScheduleExceptionPopoverComponent } from '../exception.popover/exception.popover.component';
import { ScheduleExceptionsListComponent } from '../exceptions.list/exceptions.list.component';
import { removeScheduleException, scheduleExceptionRows } from '../exceptions.list/exceptions.list.model';
import { SCHEDULE_EDITOR_LAUNCHER, ScheduleEditorDates, ScheduleEditorResult } from '../editor/schedule.editor.launcher.contract';
import { NEXT_OCCURRENCE_CHIP_LIMIT, SCHEDULE_ENGINE, SCHEDULE_TODAY, ScheduleCardContext, buildScheduleSubtitle, buildScheduleTitle, formatChipDate, upcomingOccurrences } from './schedule.card.model';
import { scheduleCardBridgeStyles, scheduleCardStyleId } from './schedule.card.theme';

/** Tooltip on the disabled Set schedule / Edit buttons when no host has provided the editor dialog. */
const LAUNCHER_UNAVAILABLE_TOOLTIP = 'editor unavailable';

/**
 * What the task form shows in its Schedule area: the schedule the task has, with its upcoming dates and
 * its exceptions — or, with no schedule of its own, the working-day default that is itself a schedule.
 *
 * Absent is not an empty state. A task with no schedule of its own is due every working day of its
 * plan, so the card renders solid, titles the rule, and shows the same Next and Exceptions rows a
 * configured schedule gets. The only tack-on state is an explicit per-visit schedule, which is a choice
 * the user made rather than the absence of one.
 *
 * The pattern editor is not built here. It arrives through {@link SCHEDULE_EDITOR_LAUNCHER}, which
 * the card injects optionally: a host that has not wired the dialog still renders the card, with its
 * launch buttons disabled and explained, instead of failing to construct. Exceptions, by contrast, are
 * authored on the card itself — that is where a scheduler reaches for them.
 */
@Component({
  selector: 'wb-schedule-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [OverlayModule, ButtonComponent, ScheduleExceptionsListComponent, ScheduleExceptionPopoverComponent],
  templateUrl: 'schedule.card.component.html',
  styleUrls: ['./schedule.card.component.less'],
})
export class ScheduleCardComponent {

  private readonly document = inject(DOCUMENT);

  private readonly todayProvider = inject(SCHEDULE_TODAY);

  private readonly launcher = inject(SCHEDULE_EDITOR_LAUNCHER, { optional: true });

  /** The task's schedule. Absent means it is due every working day of its plan. */
  public readonly $schedule = input<ScheduleRule | null | undefined>(undefined, { alias: 'schedule' });

  public readonly $exceptions = input<ScheduleExceptions | undefined>(undefined, { alias: 'exceptions' });

  /** Working week, holidays, and the line anchor the preview is evaluated against. */
  public readonly $context = input<ScheduleCardContext | null>(null, { alias: 'context' });

  /** View-only contexts render the card without Edit, + Skip or + Extra date. */
  public readonly $readonly = input<boolean>(false, { alias: 'readonly' });

  /**
   * Emitted when the editor returns a schedule, together with the two task-level dates it also edits.
   *
   * One output rather than three: the editor answers all of it in one dialog, and splitting it would
   * let a host write the schedule and drop the start override, leaving the card showing a from-clause
   * for a value nothing stored.
   */
  public readonly scheduleChange = output<ScheduleEditorResult>();

  /** Emitted when a popover authors an exception. The host applies it and writes the result back. */
  public readonly exceptionsChange = output<ScheduleExceptions>();

  protected readonly ExceptionPopoverMode = ExceptionPopoverMode;

  protected readonly launcherTooltip = LAUNCHER_UNAVAILABLE_TOOLTIP;

  protected readonly buttonSize = ButtonSize.Small;

  protected readonly secondaryStyle = ButtonDisplayStyle.Secondary;

  /** Popovers open below their trigger, flipping above when the panel would run off the bottom. */
  protected readonly overlayPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
  ];

  /** Which popover is open, or null. One signal because the two are mutually exclusive. */
  protected readonly $openPopover = signal<ExceptionPopoverMode | null>(null);

  /**
   * The input after the absent-state gate. Legacy shapes reach the card from dev data, and every one of
   * them has to fall back to the working-day default rather than to a card the engine cannot describe.
   */
  protected readonly $effectiveSchedule = computed(() => effectiveScheduleRule(this.$schedule()));

  /** Whether the task carries a schedule of its own. The default is a schedule too — just not its own. */
  protected readonly $isConfigured = computed(() => !!this.$effectiveSchedule());

  /** A per-visit schedule has a cadence but no calendar dates, so the Next row carries words, not chips. */
  protected readonly $isPerVisit = computed(() => this.$effectiveSchedule()?.kind === 'perVisit');

  protected readonly $canLaunchEditor = computed(() => !!this.launcher && !this.$readonly());

  /**
   * A hand-picked date list has no + Extra date action: the list IS the extra dates, so the popover
   * would offer a second, invisible way to add one — and the two would then disagree on the card.
   */
  protected readonly $isDates = computed(() => this.$effectiveSchedule()?.kind === 'dates');

  /** The engine's own words, plus the task's own start when it overrides the line's. */
  protected readonly $title = computed(() => buildScheduleTitle(this.$effectiveSchedule(), this.$context()?.startsOn));

  protected readonly $subtitle = computed(() => buildScheduleSubtitle(this.$effectiveSchedule(), this.$exceptions()));

  protected readonly $exceptionRows = computed(() => scheduleExceptionRows(this.$exceptions(), { removable: !this.$readonly() }));

  /** Every effective occurrence from today through year end; the row splits it into chips and a count. */
  private readonly $upcoming = computed<CalendarDate[]>(() => upcomingOccurrences(this.$effectiveSchedule(), this.$context()?.today ?? this.todayProvider(), this.$context(), this.$exceptions()));

  protected readonly $nextChips = computed(() => this.$upcoming().slice(0, NEXT_OCCURRENCE_CHIP_LIMIT).map(formatChipDate));

  protected readonly $moreCount = computed(() => Math.max(0, this.$upcoming().length - NEXT_OCCURRENCE_CHIP_LIMIT));

  public constructor() {

    DesignSystem.Mount(this.document, scheduleCardStyleId, scheduleCardBridgeStyles);
  }

  /**
   * Hands the current model to whatever editor the host provided and emits whatever comes back. A
   * cancelled dialog resolves with `undefined`, which is indistinguishable from "cleared", so a cancel
   * emits nothing at all rather than clearing the schedule the user was editing.
   */
  protected async onLaunchEditor(): Promise<void> {

    if (!this.launcher || this.$readonly()) return;

    const dates: ScheduleEditorDates = { startsOn: this.$context()?.startsOn, lastCompleted: this.$context()?.lastCompleted };
    const result = await this.launcher.open(this.$effectiveSchedule(), { readonly: false, dates });
    if (result === undefined) return;

    this.scheduleChange.emit(result);
  }

  protected onTogglePopover(mode: ExceptionPopoverMode): void {

    this.$openPopover.update(current => (current === mode ? null : mode));
  }

  protected onClosePopover(): void {

    this.$openPopover.set(null);
  }

  protected onExceptionChange(change: ScheduleChange): void {

    this.$openPopover.set(null);
    this.exceptionsChange.emit(SCHEDULE_ENGINE.applyException(this.$exceptions(), change));
  }

  protected onRemoveException(id: string): void {

    if (this.$readonly()) return;
    this.exceptionsChange.emit(removeScheduleException(this.$exceptions(), id));
  }
}
